import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import * as api from '@/lib/api';
import loginRoute from '@/pages/Login/route';
import { authActions, authSelectors } from '@/state/auth';
import { isTermsAccepted } from '@/state/auth/selectors';

interface LoginArgs {
    email: string;
    password: string;
}
interface UseAuthData {
    authed: boolean;
    authenticating: boolean;
    checkAuth: () => void;
    login: ({ email, password }: { email: string; password: string }) => Promise<boolean>;
    logout: () => void;
    termsAccepted: boolean;
    user?: api.User;
}

export const useCheckAuth = () => {
    const dispatch = useDispatch();

    const checkAuth = useCallback(async () => {
        dispatch(authActions.init());
    }, [dispatch]);
    return { checkAuth };
};

export const useAuth = (): UseAuthData => {
    const authed = useSelector(authSelectors.isAuthenticated);
    const authenticating = useSelector(authSelectors.isAuthenticating);
    const user = useSelector(authSelectors.getCurrentUser);
    const termsAccepted = useSelector(isTermsAccepted);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Check the auth token if we have one. It may have expired, or be otherwise invalid.
    const { checkAuth } = useCheckAuth();
    const login = useCallback(
        async ({ email, password }: LoginArgs): Promise<boolean> => {
            try {
                const { access_token: accessToken, refresh_token: refreshToken } = await api.login({
                    body: { username: email, password },
                });

                dispatch(authActions.login({ accessToken, refreshToken }));
                return true;
            } catch (_e) {
                dispatch(authActions.logout());
                return false;
            }
        },
        [dispatch]
    );

    const logout = useCallback((): void => {
        dispatch(authActions.logout());
        navigate(loginRoute.basePath);
    }, [dispatch, navigate]);

    return {
        authed,
        authenticating,
        checkAuth,
        login,
        logout,
        termsAccepted,
        user,
    };
};
